import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { CustomerMasterDto, JobDto, JobHoldTypeDto, JobJournalDto, LookupListEx, ModelMasterDto, ResourceMasterDto, fullName } from "@shared/models";
import { switchMap } from "rxjs/operators";
import { AppQuery } from "@modules/common/app.store";
import { dateSort } from "@modules/common";
import { CustomerService, JobService, ProductSettingService } from "../../common/services/index";
import { HoldPermission } from "../details/details.component";

@Component({
  selector: "abi-print",
  templateUrl: "./print.component.html",
  styleUrls: ["./print.component.scss"]
})
export class PrintComponent implements OnInit, OnChanges {
  @Input() job: JobDto;
  @Input() customer: CustomerMasterDto;
  @Input() resource: ResourceMasterDto;
  @Input() model: ModelMasterDto;
  @Input() jobHolds: LookupListEx<JobHoldTypeDto>;
  @Input() useRouting = true;
  @Input() sections: string[] = ["job", "tech", "customer", "dealer", "machine", "codes", "signature", "lines", "journals"];
  @Input() title: string = "Job.JobSheet";
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressLine5: string;
  postalCode: string;
  countryCode: string;
  websiteURL: string;
  registrationNumber: string;
  vatNumber: string;
  tenant: string;

  showLines = true;
  showNotes = false;
  showSignature = false;

  get allJournals(): JobJournalDto[] {
    return [...this.job?.journals || [], ...this.job?.machine?.journals || []].sort((a, b) => dateSort(a.created, b.created));
  }

  constructor(private productSettings: ProductSettingService, private route: ActivatedRoute,
              private jobService: JobService, private appQuery: AppQuery, private customerService: CustomerService) {
    this.tenant = route.snapshot.paramMap.get("tenant");
    this.productSettings
      .getCompanyInfo()
      .toPromise()
      .then(info => {
        this.companyName = info.name;
        this.addressLine1 = info.lines[0];
        this.addressLine2 = info.lines[1];
        this.addressLine3 = info.lines[2];
        this.addressLine4 = info.lines[3];
        this.addressLine5 = info.lines[4];
        this.postalCode = info.postalCode;
        this.countryCode = info.country;
        this.websiteURL = info.website;
        this.registrationNumber = info.registrationNumber;
        this.vatNumber = info.vatNumber;
        // this.email = info.emailAddress;
        // this.telephone = info.telephone;
      });

    this.showLines = !productSettings.hasSetting("WebJobCardLines") || this.productSettings.booleanValue("WebJobCardLines");
    this.showNotes = this.productSettings.booleanValue("WebJobCardNotes");
    this.showSignature = this.productSettings.booleanValue("WebJobCardSignature");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.job || changes.customer){
      this.processSignatureFile();
    }
  }

  processSignatureFile(){
    if(this.showSignature && this.job && this.job.holds && this.job.attachments?.length){
      this.signatureFileCustomer = "";
      this.signatureDateCustomer = new Date();
      this.signatureNameCustomer = "";
      // Holds Lookups Exist
      if(this.jobHolds.count()){
        const SigHold = this.jobHolds.values.find(jh => Object.keys(jh.permissions).includes(HoldPermission.SIGNATURE) && !!jh.permissions[HoldPermission.SIGNATURE]);
        if(SigHold){
          // Job Data has Holds
          if(this.job.holds?.length){
            const sigHold = this.job.holds.filter(h => h.typeId === SigHold.code).pop();
            if(sigHold) {
              if(sigHold.attachmentId){
                const sigAtt = this.job.attachments.find(att => att.attachmentId === sigHold.attachmentId);
                this.signatureFileCustomer = this.jobService.fullUrl(sigAtt, this.job.jobId);
              }
              this.signatureDateCustomer = sigHold.cleared;
              this.signatureNameCustomer = sigHold.contactId ? fullName(this.customer?.contacts.find(c => c.contactId === sigHold.contactId)) || "Customer" : sigHold.clearedUserId;
            }
          }
        }
      }
    }
  }

  signatureFileCustomer: string ="";
  signatureDateCustomer: Date = new Date();
  signatureNameCustomer: string = "";

  ngOnInit() {
    if (this.useRouting) {
      this.route.paramMap.pipe(
        switchMap((params: ParamMap) =>
          this.jobService.getJob(params.get("jobId"), params.get("check"))
        ))
        .subscribe((job: JobDto) => {
          this.job = job;

          this.processSignatureFile();
          if (!job.machine.model) {
            this.jobService
              .getModel(job.machine.modelId)
              .toPromise()
              .then(m => {
                this.model = m;
                job.machine.model = m;
              });
          }
        });
    }

    this.appQuery.$roles.subscribe(roles => {
      roles.forEach(r => {
        this.roleFieldHiddenMap[r] = this.productSettings.arrayValue("JobDetailsFieldsHidden_" + r, []);
      });
    });

    this.processSignatureFile();
  }

  public asTextBlock(): string {
    let result = this.companyName + "\n";

    if (this.addressLine1) {
      result += this.addressLine1 + "\n";
    }
    if (this.addressLine2) {
      result += this.addressLine2 + "\n";
    }
    if (this.addressLine3) {
      result += this.addressLine3 + "\n";
    }
    if (this.addressLine4) {
      result += this.addressLine4 + "\n";
    }
    if (this.postalCode) {
      result += this.postalCode;
    }
    // if (this.addressLine5)
    //   result += this.addressLine5 + '\n';

    return result;
  }

  roleFieldHiddenMap: {[role: string]: string[]} = {};
  showField(fieldName: string, defaultValue: boolean = true) {
    // Special Override that hivdes specific fields for Specific Roles
    // if all role field hidden maps are there - hide field
    if(Object.keys(this.roleFieldHiddenMap).length && this.appQuery.roles.every(role => this.roleFieldHiddenMap[role]?.includes(fieldName))) {
      return false;
    }
    return true;
  }

  showSection(section: string) {
    return this.sections.includes(section);
  }
}
